/**
 * ThreadSidebar — fullscreen thread rail (thread-workflow UX T3).
 *
 * The expanded view's answer to "Slack-grade" multi-thread work: full titles,
 * relative activity time, unread/processing signals, pin + archive
 * affordances, and the + at the top. Driven by the SAME `orderThreads` and the
 * SAME callbacks as the compact chip strip, so the two renderers can never
 * disagree about order or behavior. Hidden ≤820px (the chip strip serves
 * compact surfaces).
 *
 * Round 3 (2026-08-01): official "prest[0]" brand wordmark top-left; the list
 * splits into three lowercase sections — "routines" → "group threads" →
 * "threads" — each only when non-empty, every thread in exactly one
 * (precedence routines > group threads > threads; the group predicate is the
 * header's own participant-count source, see `participantsByThread`). Rows
 * wear type glyphs (routine / two-people / chat bubble) on the left.
 *
 * Footer (fullscreen exit + account menu, Miles 2026-07-13): pinned to the
 * rail's bottom edge — account menu (wallet / settings / sign out) and a
 * labeled "exit full screen" button, so leaving fullscreen no longer depends
 * on finding the small mode-cycle icon top-right.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ThreadSidebarContainer,
  ThreadSidebarHeader,
  ThreadSidebarList,
  ThreadSidebarRow,
  ThreadSidebarRowMain,
  ThreadSidebarRowTitle,
  ThreadSidebarRowMeta,
  ThreadSidebarFooter,
  SidebarAccountButton,
  SidebarAccountAvatar,
  SidebarAccountName,
  SidebarAccountMenu,
  SidebarAccountMenuItem,
  SidebarBrandWordmark,
  SidebarBrandLogoImage,
  SidebarExitFullScreenButton,
  ThreadUnreadBadge,
  ThreadChipPinButton,
  ThreadChipCloseButton,
  ThreadNewButton,
} from './styles';
import { CollapseSvgIcon } from './components';
import type { ChatThread } from './types';
import { ARCHIVE_ALWAYS_AVAILABLE, hasUnseenActivity, orderThreads } from './useChatThreads';
import { effectiveUnreadCount } from './threadArrival';
import {
  ThreadTurnIndicator,
  relativeActivityLabel,
  shouldRouteCloseToEmptyDelete,
} from './ThreadSwitcherBar';
import { RoutineGlyph, routineNextRunLabel, routineThreadDisplayName } from './RoutineGlyph';
import { ThreadTypeGlyph } from './ThreadTypeGlyph';
import { ThreadSectionHeader } from './ThreadSectionHeader';
import { ThreadRowActions, ThreadRowRenameInput } from './ThreadRowActions';
import { useThreadDrag } from './useThreadDrag';
import { ThreadListNotice } from './ThreadListNotice';
import { threadListCopy } from './threadListCopy';
import type { ThreadOrganizeApi } from './threadOrganize';
import { ThreadSearch, type ThreadSearchApi } from './ThreadSearch';
import { isGroupParticipantCount } from '../../services/groupThreads/senderIdentity';
import { buildThreadSections, sidebarSectionHook, type ThreadSection } from './threadSections';
import { canOrganizeThreads } from './threadOrganize';
import {
  threadDisplayKindFor,
  threadDisplayTitleFor,
  type ThreadListIdentityProps,
} from './threadListIdentity';
import type { ThreadRoutineMirrorPatch } from './useChatThreads';
import {
  buildRoutineManagerRows,
  groupRoutineRowsByThread,
  type RoutineManagerRowView,
  type RoutineThreadGroupView,
} from './routineRows';
import { useAccountRoutines } from './useAccountRoutines';
import {
  SidebarSectionHeader,
  SidebarSectionCount,
  RoutineManageButton,
  RoutineRowTitleLine,
  RoutineNextRunMeta,
  RoutinePausedChip,
} from './Routines.styles';
import RoutineManagerPanel from './RoutineManagerPanel';
import styled from 'styled-components';
import {
  fetchWorkspaceOptionsCached,
  patchCachedDisplayNames,
  revalidateWorkspaceOptions,
} from '../../services/workspaceOptionsCache';
import { subscribeWorkspaceNames } from '../../services/workspaceIndexLive';
import { workspaceLabel, workspaceMarkColor } from '../../services/workspaceIdentity';
import type { WorkspaceOption } from '../../services/rbacService';

// ── Workspace header (owner directive 2026-08-19 — supersedes BOTH the
// ghost "all workspaces" pill of multi-org P4 AND #909's removal of the
// combined view; containment holds because foreign rows run the sealed
// organization switch, never an in-place open) ───────────────────────────────
// ONE cohesive block at the rail top: the organization's identity (dot +
// name, house gold at full opacity — invariant 3: every screen carries
// unmistakable workspace identity) with the view toggle IN the same block, as
// a proper high-contrast segmented control ("this workspace" | "all
// workspaces · N"). The old dashed ghost button was nearly invisible on the
// dark rail and floated detached inside the thread list. The combined state
// stays TRANSIENT — plain component state, so a workspace switch (hard
// navigation) or a fresh sign-in always boots back to the separated default
// ("the default is always ever separating").
const WorkspaceHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 12px 4px;
  padding: 3px 0 10px;
  border-bottom: 1px solid rgba(198, 167, 94, 0.35);
`;
const WorkspaceIdentityLine = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--brand-gold);
  overflow: hidden;
  white-space: nowrap;
`;
const WorkspaceIdentityName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
const WorkspaceContextDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${(p) => p.$color};
  /* Definition ring so the mark holds contrast on the dark rail. */
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
`;
const WorkspaceViewToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  border: 1px solid rgba(198, 167, 94, 0.45);
  border-radius: 999px;
  overflow: hidden;
`;
const WorkspaceViewSegment = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: none;
  margin: 0;
  padding: 4px 11px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.7rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  letter-spacing: 0.02em;
  line-height: 1.4;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  /* Real contrast in BOTH states (owner directive 2026-08-19: "needs more
     contrast"): the active segment is gold-filled with near-black text; the
     rest segment keeps fully legible light text — never ghost opacity. */
  background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-black)' : 'rgba(250, 250, 248, 0.88)')};
  transition: background 0.18s ease, color 0.18s ease;
  &:hover { background: ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.16)')}; }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.8); outline-offset: -2px; }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
// Foreign-row switch affordance (combined view): a small trailing mark so a
// row that will CHANGE organizations reads as such before the click.
const ForeignSwitchGlyph = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: rgba(198, 167, 94, 0.85);
  svg { display: block; }
`;

/**
 * The OFFICIAL brand asset (round 3, 2026-08-01) — byte-identical src to the
 * site Header / LandingPage / GlobalFooter / PageLoader `prest[0]` treatment,
 * so the sidebar can never drift onto a stray logo variant. Exported for the
 * asset-pin test.
 */
export const SIDEBAR_BRAND_LOGO_SRC = '/images/project-olivia-logo.webp?v=20260715';

// relativeActivityLabel moved to ThreadSwitcherBar (2026-07-13) so the chip
// tooltip meta and the sidebar row meta share one implementation; re-exported
// here for existing callers/tests.
export { relativeActivityLabel } from './ThreadSwitcherBar';

/**
 * Signed-in identity for the footer's account menu button (fullscreen exit +
 * account menu, Miles 2026-07-13). Derived by the host from the SAME
 * AuthContext the app header uses — the sidebar stays presentational.
 */
export interface SidebarAccountIdentity {
  /** Display name rendered beside the avatar (ellipsizes). */
  name: string;
  /** 1–2 character lowercase initials fallback when no image is available. */
  initials: string;
  /** Profile image URL; the initials render when absent. */
  avatarUrl?: string;
}

export interface ThreadSidebarProps extends ThreadListIdentityProps {
  /**
   * THREAD SEARCH (PR-5, 2026-09-09): the host's search API — instant tier
   * over what the browser holds, deep tier on Enter, a hit opens its thread
   * (and scrolls to the message). Absent → no search box (lever off, guests).
   */
  search?: ThreadSearchApi | null;
  /** The organization surface (2026-09-09): section rename / move / collapse / delete, move-to from any row, drag onto a section, undo. */
  organize?: ThreadOrganizeApi | null;
  threads: ChatThread[];
  activeThreadId: string | null;
  /** The resolved active workspace (drives own-vs-foreign row behavior). */
  activeWorkspaceId?: string | null;
  /**
   * EXTENDED VIEW (owner directive 2026-08-19 — supersedes #909's removal):
   * EVERY workspace's threads (unfiltered) for the explicit org-grouped
   * extended view only — `threads` above stays workspace-separated. Omitted
   * → no extended-view affordance renders (strict-containment callers
   * unchanged).
   */
  allThreads?: ChatThread[];
  /**
   * Open a thread bound to ANOTHER workspace: the host runs the SEALED
   * organization switch (blocking ≥3s overlay → server-verified switch →
   * pending-open marker → hard navigation). A foreign thread NEVER renders
   * in place (containment invariant preserved).
   */
  onOpenForeignThread?: (thread: ChatThread) => void;
  /**
   * threadId → participant count (round 3, 2026-08-01) — the SAME
   * `sharedThreadParticipants` map the chat header's "Just you"/"N people"
   * line reads (GlobalChatSystem hydrates it from the server-mediated
   * membership registry via the fail-closed `threadMembershipRead` CF, on
   * every device). The "group threads" section is derived from it through
   * `isGroupParticipantCount` — the header label's own numeric rule — so the
   * section split can never disagree with the header. A thread absent from
   * the map counts 1 (solo), exactly like the header. Omitted → no group
   * section (every non-routine thread is an individual thread).
   */
  participantsByThread?: Record<string, number>;
  /**
   * Per-thread processing map — the ONE driver of the uniform in-summary
   * activity indicator, for EVERY row including the active one. See the
   * `ThreadSwitcherBarProps.processingByThread` doc (identical semantics;
   * the two surfaces mirror each other).
   */
  processingByThread?: Record<string, boolean>;
  /**
   * Per-thread turn-start epochs for the ticking elapsed clock — see
   * `ThreadSwitcherBarProps.turnStartedAtByThread`.
   */
  turnStartedAtByThread?: Record<string, number>;
  /**
   * DEPRECATED — accepted and IGNORED (owner override, Miles 2026-08-01).
   * Identical history + semantics to
   * `ThreadSwitcherBarProps.activePaneOverlayVisible` (the two surfaces
   * mirror each other): the 2026-07-15 "pane-owns-active clock" rule made
   * the active row hide its spinner/clock while the pane overlay was up;
   * the 2026-08-01 override removes that suppression — the row shows the
   * working indicator in ALL views/modes, active thread included, and two
   * clocks (pane + row) are intentional. Kept (ignored) for call-site
   * compatibility; remove on the next breaking pass.
   */
  activePaneOverlayVisible?: boolean;
  /**
   * Live USER-message count for the active thread — see the
   * `ThreadSwitcherBarProps.activeThreadMessageCount` doc (same predicate,
   * `shouldRouteCloseToEmptyDelete`, drives both surfaces).
   */
  activeThreadMessageCount?: number;
  /**
   * Navigate to a thread. Server-only routine rows (routine list parity,
   * 2026-08-02) pass `{ fallbackTitle: routine name }` so the host's
   * navigation hydration can label the destination when its index doc is
   * missing/archived; ordinary rows pass the id alone.
   */
  onSwitch: (threadId: string, options?: { fallbackTitle?: string }) => void;
  onNew: () => void;
  onRequestDelete: (threadId: string) => void;
  onDeleteEmptyThread?: (threadId: string) => void;
  onTogglePin?: (threadId: string) => void;
  /**
   * Footer account menu (fullscreen exit + account menu, Miles 2026-07-13):
   * signed-in identity. Omitted/null (guest) hides the account button; the
   * exit button still renders.
   */
  account?: SidebarAccountIdentity | null;
  /** Account menu → wallet (host navigates to /wallet). */
  onNavigateWallet?: () => void;
  /** Account menu → settings (host navigates to /settings). Omit to hide. */
  onNavigateSettings?: () => void;
  /** Account menu → sign out (the existing AuthContext logout). */
  onSignOut?: () => void;
  /**
   * Footer exit button. MUST be the same handler the header's mode-cycle
   * button uses to leave fullscreen (one source of truth — the sidebar owns
   * no mode state). Omitted → no exit button (isolated tests).
   */
  onExitFullScreen?: () => void;
  /**
   * Host patch into the LOCAL thread record's routine mirror
   * (threadStore.patchThreadRoutineMirror) — passed straight through to the
   * routine manager panel so its pause/resume successes and authoritative
   * list snapshot keep the rail/header in step (2026-08-01 drift fix).
   */
  onRoutineMirrorPatch?: (threadId: string, patch: ThreadRoutineMirrorPatch) => void;
  /**
   * Signed-in uid (adversarial review finding 5, 2026-08-03). Passed straight
   * into `useAccountRoutines` to SCOPE its cross-mount snapshot cache: this
   * sidebar is mounted conditionally on fullscreen, so every exit/entry
   * remounts the hook, and without the cache each toggle costs a fresh
   * listRoutines() plus a visible routines-count flicker. Omitted → caching
   * disabled (guests have no account routines; isolated tests stay cold).
   */
  accountUid?: string | null;
  /**
   * Is this thread id LOCALLY tombstoned — i.e. archived on this device
   * within the tombstone TTL (adversarial review finding 4, 2026-08-03)?
   * Supplied by the host from `isThreadTombstonedInStorage`. The routine
   * union is built from the account list, which can still carry a routine the
   * user archived moments ago (a stale retained snapshot, or a server list
   * that has not healed yet); without this filter that routine renders as a
   * clickable ghost row in the rail. Omitted → no filtering (isolated tests).
   */
  isThreadTombstoned?: (threadId: string) => boolean;
}

/**
 * Footer account button + its popover menu. The popover portals to
 * document.body at BODY_PORTAL_Z_INDEX (fullscreen burial lesson — see the
 * constant's doc in styles.ts) and opens UPWARD from the bottom-left button.
 * Keyboard contract: opening focuses the first menu item, Escape closes and
 * returns focus to the button, outside pointer-down closes.
 */
const SidebarAccountMenuSection: React.FC<{
  account: SidebarAccountIdentity;
  onNavigateWallet?: () => void;
  onNavigateSettings?: () => void;
  onSignOut?: () => void;
}> = ({ account, onNavigateWallet, onNavigateSettings, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number }>({ left: 12, bottom: 60 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((refocusButton: boolean) => {
    setOpen(false);
    if (refocusButton) buttonRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    if (open) {
      close(false);
      return;
    }
    // Anchor above the button, clamped to the viewport (mirrors the
    // ThreadChipTooltip fixed-coordinate approach).
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      const menuWidth = 216; // min-width 208 + padding allowance
      setPos({
        left: Math.max(8, Math.min(rect.left, (window.innerWidth || menuWidth) - menuWidth - 8)),
        bottom: Math.max(8, (window.innerHeight || 0) - rect.top + 6),
      });
    }
    setOpen(true);
  }, [open, close]);

  // Focus the first menu item on open so the menu is reachable by keyboard
  // (the portal would otherwise put it at the end of the tab order).
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  // Escape closes + refocuses the button; outside pointer-down closes.
  // Document-level listeners because the menu lives in a body portal.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [open, close]);

  const pick = useCallback((action?: () => void) => () => {
    close(false);
    action?.();
  }, [close]);

  return (
    <>
      <SidebarAccountButton
        ref={buttonRef}
        type="button"
        data-sidebar-account-button=""
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`account menu for ${account.name}`}
        title="account"
        onClick={toggle}
      >
        <SidebarAccountAvatar aria-hidden="true">
          {account.avatarUrl
            ? <img src={account.avatarUrl} alt="" loading="lazy" />
            : account.initials}
        </SidebarAccountAvatar>
        <SidebarAccountName>{account.name}</SidebarAccountName>
      </SidebarAccountButton>
      {open && typeof document !== 'undefined' && createPortal(
        <SidebarAccountMenu
          ref={menuRef}
          role="menu"
          aria-label="account"
          data-sidebar-account-menu=""
          /* Body portal marker (portalClickGuard contract) — without it the
             chat's outside-click handler closes the pane on any item tap. */
          data-prest0n-portal="true"
          $left={pos.left}
          $bottom={pos.bottom}
        >
          {onNavigateWallet && (
            <SidebarAccountMenuItem
              type="button"
              role="menuitem"
              data-account-menu-wallet=""
              onClick={pick(onNavigateWallet)}
            >
              wallet
            </SidebarAccountMenuItem>
          )}
          {onNavigateSettings && (
            <SidebarAccountMenuItem
              type="button"
              role="menuitem"
              data-account-menu-settings=""
              onClick={pick(onNavigateSettings)}
            >
              settings
            </SidebarAccountMenuItem>
          )}
          {onSignOut && (
            <SidebarAccountMenuItem
              type="button"
              role="menuitem"
              data-account-menu-signout=""
              onClick={pick(onSignOut)}
            >
              sign out
            </SidebarAccountMenuItem>
          )}
        </SidebarAccountMenu>,
        document.body,
      )}
    </>
  );
};

export const ThreadSidebar: React.FC<ThreadSidebarProps> = ({
  threads,
  activeThreadId,
  participantsByThread,
  processingByThread,
  turnStartedAtByThread,
  activeThreadMessageCount,
  onSwitch,
  onNew,
  onRequestDelete,
  onDeleteEmptyThread,
  onTogglePin,
  participantIdsByThread,
  memberLabels,
  categories,
  search,
  organize,
  account,
  onNavigateWallet,
  onNavigateSettings,
  onSignOut,
  onExitFullScreen,
  onRoutineMirrorPatch,
  accountUid,
  isThreadTombstoned,
  activeWorkspaceId,
  allThreads,
  onOpenForeignThread,
}) => {
  const ordered = orderThreads(threads);
  // Extended view (owner directive 2026-08-19): transient by design — resets
  // to the separated default on every mount (workspace switches hard-navigate).
  const [combinedView, setCombinedView] = useState(false);
  const [workspaceOptions, setWorkspaceOptions] = useState<WorkspaceOption[] | null>(null);
  const combinedRows = combinedView && allThreads ? orderThreads(allThreads) : null;
  const foreignThreadCount = allThreads
    ? allThreads.filter((t) => t.accountId && activeWorkspaceId && t.accountId !== activeWorkspaceId).length
    : 0;
  // One shared snapshot (workspaceOptionsCache) on mount: feeds the always-on
  // identity chip AND the combined-view badges. Tolerant: null renders no
  // identity chrome, never a broken rail. ORG-NAME PARITY (owner report
  // 2026-08-19: extended view labeled real organizations "another
  // workspace"): mirror WorkspacePill — a background revalidation chases the
  // cached paint so a stale (up to 7-day) snapshot missing a newly-joined
  // org converges without a reload.
  useEffect(() => {
    let alive = true;
    // The uid unlocks the persisted instant paint (speed round 2026-08-16).
    fetchWorkspaceOptionsCached(accountUid ?? null)
      .then((options) => { if (alive) setWorkspaceOptions(options); })
      .catch(() => { if (alive) setWorkspaceOptions([]); });
    revalidateWorkspaceOptions(accountUid ?? null)
      .then((options) => { if (alive && options.length > 0) setWorkspaceOptions(options); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [accountUid]);
  // Live rename/name channel (same source as WorkspacePill): the server keeps
  // users/{uid}/workspace_index current for every membership, so it names
  // organizations even when the cached options list is stale — and renames
  // repaint the rail in real time. The shared cache is patched so the next
  // paint anywhere starts from the fresh labels too.
  const [liveWorkspaceNames, setLiveWorkspaceNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!accountUid) return;
    return subscribeWorkspaceNames(accountUid, (names) => {
      setLiveWorkspaceNames(names);
      patchCachedDisplayNames(accountUid, names);
    });
  }, [accountUid]);
  const displayedWorkspaceOptions = useMemo(
    () => (workspaceOptions
      ? workspaceOptions.map((w) => (
        liveWorkspaceNames[w.accountId] ? { ...w, displayName: liveWorkspaceNames[w.accountId] } : w
      ))
      : null),
    [workspaceOptions, liveWorkspaceNames],
  );
  const activeWorkspaceOption =
    displayedWorkspaceOptions?.find((w) => (activeWorkspaceId ? w.accountId === activeWorkspaceId : w.isActive)) ?? null;
  // (Per-row badge labels retired 2026-08-19 — the extended view now groups
  // rows under per-organization section headers; labels resolve in-render.
  // Still NEVER a raw account id — the 2026-08-09 raw-redirect lesson.)
  const listRef = useRef<HTMLDivElement>(null);
  // Sections (round 3, 2026-08-01 — supersedes the 2026-07-31 two-section
  // split): every thread lands in EXACTLY ONE of three sections, precedence
  //   routines > group threads > threads
  // — a routine thread lives under "routines" regardless of group-ness, a
  // non-routine thread with ≥2 registered participants (the header's own
  // "Just you" source, see the participantsByThread prop doc) under
  // "group threads", everything else under "threads" (only threads the
  // individual user is in). A section renders ONLY when non-empty; ordering
  // WITHIN each section stays `orderThreads` (the shared rule).
  // The server-owned `sharedThread` index marker also qualifies (live-
  // membership surfacing, 2026-08-16): a thread the server just pushed into
  // this user's rail sections as a group thread IMMEDIATELY, before the
  // pull-based participant-count map has re-read the membership registry.
  const isGroupThread = (t: ChatThread): boolean =>
    t.sharedThread === true || isGroupParticipantCount(participantsByThread?.[t.id] ?? 1);
  // "similar name" pills REMOVED (owner directive 2026-07-31, round 2): the
  // duplicate-title hint wasn't actionable (no merge/rename affordance) and
  // read as clutter, so no per-row hint renders until a real disambiguation
  // action ships. Do not re-add a passive pill here.
  const [routineManagerOpen, setRoutineManagerOpen] = useState(false);

  // ROUTINE LIST PARITY (product directive, 2026-08-02): the rail's routines
  // section and the manager panel must show the SAME membership and count,
  // always. The sidebar used to render only routine threads inside the local
  // listen window (top-40 by lastActiveAt + keep-newest cap) while the
  // manager unioned the server list — "1 in the rail, 4 in the popup". Now
  // ONE hook instance owns the account `listRoutines()` snapshot (fetch on
  // mount, on manager open, after actions, after a routine thread departs),
  // and BOTH surfaces derive their rows from the ONE shared builder
  // (routineRows.ts) over the same inputs. The rail never blocks on the
  // network: thread rows render immediately in every state. A failed fetch
  // KEEPS the last successful snapshot (adversarial review finding 2,
  // 2026-08-03 — blanking it collapsed the rail back to "1 routine" on any
  // blip); only a failure with no prior success degrades to thread-sourced
  // rows, which is the original contract.
  const accountRoutines = useAccountRoutines({ threads, uid: accountUid, onRoutineMirrorPatch });
  // TOMBSTONE FILTER (adversarial review finding 4, 2026-08-03): a routine the
  // user just archived can still sit in the account snapshot (a retained
  // stale list after a failed refresh, or a server list that has not healed
  // yet). Strip locally-tombstoned ids from BOTH union inputs before building
  // rows, and hand the manager panel the SAME filtered inputs so the two
  // surfaces stay at parity by construction.
  const routineSourceThreads = isThreadTombstoned
    ? threads.filter((t) => !isThreadTombstoned(t.id))
    : threads;
  const visibleAccountRoutines = isThreadTombstoned && accountRoutines.routines
    ? {
        ...accountRoutines,
        routines: accountRoutines.routines.filter((s) => !isThreadTombstoned(s.threadId)),
      }
    : accountRoutines;
  const routineUnionRows = buildRoutineManagerRows(
    routineSourceThreads,
    visibleAccountRoutines.routines,
  );
  // v2 (multi-routine threads): the union is per-ROUTINE (parity unit — the
  // manager shows it flat); the rail lists THREADS, so its rows are the
  // per-thread grouping of the same union. Same membership, two projections.
  const routineThreadGroups = groupRoutineRowsByThread(routineUnionRows);
  const threadsById = new Map(threads.map((t) => [t.id, t] as const));
  // SECTION EXCLUSIVITY (adversarial review finding 3, 2026-08-03). The
  // section predicates used to disagree with the union: group/chat rows
  // filtered on `!t.routineId`, while the union emits a SERVER row for any
  // listed threadId without a routine-bearing local record. A thread whose
  // local record lacks `routineId` but whose id is still in the account
  // snapshot therefore rendered in TWO sections at once — both computing
  // isActive from the same activeThreadId (two "active" rows), and counted by
  // the routines badge as well. The union is now built FIRST and the other
  // sections exclude its thread ids, so every id lands in exactly one section
  // and the precedence (routines > group threads > threads) is unchanged.
  // Deliberately NOT fixed by widening `liveIds` inside routineRows.ts: that
  // would drop server rows the manager panel legitimately needs.
  // (`!t.routineId` is kept alongside the union exclusion so a routine thread
  // the tombstone filter removed from the union cannot fall THROUGH into the
  // chat/group sections — an archived routine belongs in no section at all.)
  const routineRowIds = new Set(routineUnionRows.map((r) => r.threadId));
  const isRoutineSectionThread = (t: ChatThread): boolean =>
    routineRowIds.has(t.id) || !!t.routineId;
  // PR-5 (2026-09-09): the non-routine rows section through the ONE shared
  // builder — the viewer's categories first, then direct messages (exactly
  // two humans, named after the other person), then group threads, then
  // threads. `isGroupThread` above stays the routine rows' paired-glyph
  // predicate; the builder's kind resolver reads the SAME count map plus the
  // participant ids, so a two-person thread can never section as a group
  // thread on one surface and a direct message on another.
  const listIdentity: ThreadListIdentityProps = {
    participantIdsByThread,
    memberLabels,
    categories,
  };
  // Every section, the empty ones included — the rail renders the non-empty
  // ones; the full list feeds "manage sections…" (2026-09-09), where an empty
  // section can still be moved, renamed or deleted.
  const allSections: ThreadSection<ChatThread>[] = buildThreadSections({
    rows: ordered.filter((t) => !isRoutineSectionThread(t)),
    categories: organize?.categories ?? categories,
    kindOf: (t) => threadDisplayKindFor(t, listIdentity, accountUid, participantsByThread),
    lang: organize?.lang,
    foldAliases: organize?.foldAliases,
    includeEmpty: true,
  });
  const threadSections = allSections.filter((s) => s.rows.length > 0);
  // A header's move up / down is relative to what the viewer can SEE.
  const visibleSectionIds = threadSections.map((s) => s.categoryId);
  // The organization surface (2026-09-09): inline row rename, drag onto a section.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const mayOrganize = canOrganizeThreads(organize);
  const drag = useThreadDrag(organize);
  const organizeCopy = threadListCopy(organize?.lang ?? 'en');

  // Navigability (2026-07-13): arrow keys move focus along the vertical
  // tablist — mirror of the chip strip's ArrowLeft/ArrowRight handler.
  // Focus only; activation stays native (Enter/Space on the focused row).
  const handleListKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const el = listRef.current;
    if (!el) return;
    const rows = Array.from(el.querySelectorAll<HTMLElement>('[data-sidebar-thread-id]'));
    const current = rows.indexOf(document.activeElement as HTMLElement);
    if (current === -1) return;
    e.preventDefault();
    const next = e.key === 'ArrowDown'
      ? Math.min(rows.length - 1, current + 1)
      : Math.max(0, current - 1);
    rows[next]?.focus();
  }, []);

  const handleClose = useCallback((e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const routeToEmptyDelete = shouldRouteCloseToEmptyDelete({
      threadId,
      activeThreadId,
      threads,
      activeThreadMessageCount,
      hasEmptyDeleteHandler: !!onDeleteEmptyThread,
    });
    if (routeToEmptyDelete && onDeleteEmptyThread) {
      onDeleteEmptyThread(threadId);
      return;
    }
    onRequestDelete(threadId);
  }, [activeThreadId, threads, activeThreadMessageCount, onDeleteEmptyThread, onRequestDelete]);

  const renderThreadRow = (thread: ChatThread, routineGroup?: RoutineThreadGroupView): React.ReactNode => {
    const isActive = thread.id === activeThreadId;
    const routineView: RoutineManagerRowView | undefined = routineGroup?.primary;
    // Routine Threads (2026-07-31): routine rows wear the glyph, a
    // Paused chip when dormant, and a next-run meta line instead of the
    // generic activity meta. Everything else (indicator, badge, pin, ×)
    // is IDENTICAL to a normal row — one row component, two meta skins.
    // Round 3 (2026-08-01): non-routine rows wear a type glyph
    // (two-people for group threads, chat bubble for individual threads).
    // v2 (2026-08-16): group-ness is computed INDEPENDENTLY of routine-ness —
    // a routine row that is a group thread wears the two-people glyph BESIDE
    // the routine glyph (the directive's "group icon also indicated").
    // A row rendered from a routine GROUP is a routine row even while the
    // local record's mirror has not landed (the union is authoritative — the
    // exact server-row-without-local-mirror class of finding 3).
    const isRoutine = !!thread.routineId || !!routineGroup;
    const isGroup = isGroupThread(thread);
    // PR-5: a non-routine row's glyph is its display KIND (direct / group /
    // individual) — the same resolver that sectioned it.
    const rowKind = isRoutine ? null : threadDisplayKindFor(thread, listIdentity, accountUid, participantsByThread);
    // Routine list parity (2026-08-02): when the shared builder supplied
    // union rows for this thread, render from them (the authoritative list
    // wins) so the rail can never show "active" for a routine the manager
    // knows is paused. v2 aggregate: paused only when EVERY routine here is.
    const routinePaused = routineGroup
      ? routineGroup.rows.every((r) => r.paused)
      : routineView
        ? routineView.paused
        : thread.routineStatus === 'paused';
    const routineNextRunAt = routineView !== undefined ? routineView.nextRunAt : thread.routineNextRunAt;
    // v2: how many routines live here (group rows when present; the mirror's
    // routineCount otherwise; pre-v2 mirrors read as 1).
    const routineCountHere = routineGroup ? routineGroup.rows.length : (thread.routineCount ?? (isRoutine ? 1 : 0));
    // Naming coherence (2026-08-01): routine rows are called by their
    // routine NAME (title fallback) — the one shared rule
    // (routineThreadDisplayName), so the rail, header, and manager can
    // never disagree about what a routine is called. Chat rows keep title.
    // PR-5 (2026-09-09): chat rows are called by the ONE display-title rule
    // (a human's unified rename > the other person in a direct message > the
    // record's own title) — see services/groupThreads/threadDisplayTitle.
    const displayTitle = isRoutine
      ? routineThreadDisplayName(thread)
      : threadDisplayTitleFor(thread, listIdentity, accountUid);
    // UNIFORM in-summary activity indicator (owner directive, Miles
    // 2026-07-14 — supersedes the 2026-07-12 "pane-owns-active" rule):
    // identical rule to the chip strip — EVERY processing row, active
    // included, shows the one spinner + elapsed-clock indicator driven
    // by processingByThread[thread.id]. One code path, never a double
    // spinner; the pane's own widgets are content, not rail state.
    //
    // OWNER OVERRIDE (Miles 2026-08-01) — mirror of the chip strip: the
    // active row keeps its indicator even while the active thread's pane
    // overlay shows its own "working · 0:07". The old
    // `activePaneOverlayVisible` suppression (2026-07-15 "pane-owns-active
    // clock" rule) is deliberately GONE; two clocks (pane + row) are now
    // intentional — the row is the at-a-glance status surface.
    const isTurnRunning = !!(processingByThread && processingByThread[thread.id]);
    const showTurnIndicator = isTurnRunning;
    const turnSinceMs = showTurnIndicator
      ? (turnStartedAtByThread?.[thread.id] ?? null)
      : null;
    // Phantom-proof count (2026-09-08): zero when the viewer has viewed the
    // thread since its last activity, whatever a stale counter stored.
    const unread = effectiveUnreadCount(thread);
    // PR-3 (2026-09-08): the OPEN thread keeps its badge until the viewer has
    // dwelt on it for 3 s (markThreadViewed) — opening is not reading.
    const showBadge = unread > 0;
    // Per-thread badges (2026-09-08): DOT when only the server-driven
    // "someone else was here" predicate holds (accountUid = the viewer).
    const showDot = !showBadge && hasUnseenActivity(thread, activeThreadId, accountUid);
    // EMPTY-META SUPPRESSION (adversarial review finding 7, 2026-08-03): an
    // active routine with no nextRunAt yields '' — render nothing rather than
    // an empty block holding vertical space. Same rule on the server row.
    // v2: an active TRIGGER routine has no clock — its cadence line is the
    // trigger summary ("Every time a new email arrives"); multi-routine
    // threads lead with the count.
    const primaryCadence = routinePaused
      ? 'Paused'
      : routineNextRunLabel(routineNextRunAt)
        || ((routineView?.kind === 'trigger' && routineView.scheduleSummary)
              || (!routineView && thread.routineNextRunAt == null && thread.routineScheduleSummary)
            ? (routineView?.scheduleSummary || thread.routineScheduleSummary || '')
            : '');
    const routineMetaText = routineCountHere > 1
      ? [`${routineCountHere} routines`, primaryCadence].filter(Boolean).join(' · ')
      : primaryCadence;
    return (
      <ThreadSidebarRow
        key={thread.id}
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={
          `${isRoutine ? 'routine ' : ''}${isRoutine && isGroup ? 'group thread ' : ''}${displayTitle}${isTurnRunning ? ' (working)' : ''}` +
          (showBadge ? `, ${unread} unread` : showDot ? ', new activity' : '')
        }
        data-sidebar-thread-id={thread.id}
        data-routine-row-source={isRoutine ? 'thread' : undefined}
        $active={isActive}
        title={thread.topicSummary || displayTitle}
        {...drag.rowProps(thread.id, isRoutine || renamingId === thread.id)}
        onDoubleClick={mayOrganize && organize && !isRoutine ? (e) => { e.preventDefault(); setRenamingId(thread.id); } : undefined}
        onClick={() => {
          if (!isActive) onSwitch(thread.id);
        }}
      >
        <ThreadSidebarRowMain>
          {isRoutine ? (
            <>
              <RoutineRowTitleLine>
                <RoutineGlyph active={!routinePaused} size={12} />
                {/* v2 (2026-08-16): a routine GROUP thread also wears the
                    two-people glyph — moving to the routines section must
                    not hide that people share this thread. */}
                {isGroup && (
                  <ThreadTypeGlyph kind="group" active={isActive} size={12} data-routine-group-glyph="" />
                )}
                <ThreadSidebarRowTitle $active={isActive}>{displayTitle}</ThreadSidebarRowTitle>
                {routinePaused && (
                  <RoutinePausedChip data-routine-paused-chip="">Paused</RoutinePausedChip>
                )}
              </RoutineRowTitleLine>
              {routineMetaText && (
                <RoutineNextRunMeta data-routine-row-meta="">
                  {routineMetaText}
                </RoutineNextRunMeta>
              )}
            </>
          ) : (
            <>
              <RoutineRowTitleLine>
                <ThreadTypeGlyph kind={rowKind ?? (isGroup ? 'group' : 'individual')} active={isActive} size={12} />
                {mayOrganize && organize && renamingId === thread.id ? (
                  <ThreadRowRenameInput
                    initial={displayTitle}
                    ariaLabel={organizeCopy.rename}
                    onCommit={(title) => { setRenamingId(null); organize.renameThread(thread.id, title); }}
                    onCancel={() => setRenamingId(null)}
                  />
                ) : (
                  <ThreadSidebarRowTitle $active={isActive}>{displayTitle}</ThreadSidebarRowTitle>
                )}
              </RoutineRowTitleLine>
              <ThreadSidebarRowMeta>
                {relativeActivityLabel(thread.lastActiveAt)}
                {thread.messageCount > 0 ? ` · ${thread.messageCount} messages` : ' · empty'}
              </ThreadSidebarRowMeta>
            </>
          )}
        </ThreadSidebarRowMain>
        {showTurnIndicator && (
          <ThreadTurnIndicator sinceMs={turnSinceMs} />
        )}
        {showBadge && (
          <ThreadUnreadBadge aria-hidden="true" data-unread-count={unread} $placement="inset">
            {unread > 99 ? '99+' : String(unread)}
          </ThreadUnreadBadge>
        )}
        {showDot && (
          <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="inset" />
        )}
        {mayOrganize && organize && !isRoutine && (
          <ThreadRowActions
            thread={thread}
            displayTitle={displayTitle}
            organize={organize}
            onStartRename={() => setRenamingId(thread.id)}
            onTogglePin={onTogglePin}
            onArchive={(id) => onRequestDelete(id)}
          />
        )}
        {onTogglePin && (
          <ThreadChipPinButton
            type="button"
            data-thread-pin-btn=""
            data-pinned={thread.pinned === true ? 'true' : 'false'}
            $pinned={thread.pinned === true}
            tabIndex={-1}
            aria-pressed={thread.pinned === true}
            aria-label={thread.pinned ? `unpin thread ${thread.title}` : `pin thread ${thread.title} to the top`}
            title={thread.pinned ? 'unpin thread' : 'pin thread to top'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTogglePin(thread.id);
            }}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M7.2 1.2 L10.8 4.8 L8.6 5.2 L6.4 7.4 L6 10.8 L3.8 8.6 L1.2 11.2 M3.8 8.6 L1.6 6.4 L5 6 L7.2 3.8 Z"
                fill={thread.pinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
          </ThreadChipPinButton>
        )}
        {ARCHIVE_ALWAYS_AVAILABLE && (
          <ThreadChipCloseButton
            type="button"
            data-thread-close-btn=""
            /* Dark-rail contrast variant (round 3, 2026-08-01): the light
               strip's near-black square was invisible on the dark rail. */
            $onDark
            tabIndex={-1}
            aria-label={`archive thread ${thread.title} (Prest0n keeps it in memory for reference; the conversation closes)`}
            title="archive thread"
            onClick={(e) => handleClose(e, thread.id)}
          >
            <svg viewBox="0 0 12 12" aria-hidden="true">
              <path d="M3 3 L9 9 M9 3 L3 9" strokeLinecap="round" />
            </svg>
          </ThreadChipCloseButton>
        )}
      </ThreadSidebarRow>
    );
  };

  /**
   * SERVER-ONLY routine row (routine list parity, 2026-08-02): a live
   * routine whose thread sits outside the local listen window — no thread
   * record, so no spinner/unread/pin/× (nothing local to drive them). Glyph
   * + routine name + schedule/status meta, Paused chip per the existing
   * pattern; click navigates via the SAME onSwitch, passing the routine
   * name as the hydration fallback title. Wears data-sidebar-thread-id so
   * arrow-key navigation treats it like every other row.
   */
  const renderServerRoutineRow = (group: RoutineThreadGroupView): React.ReactNode => {
    const row = group.primary;
    const isActive = row.threadId === activeThreadId;
    // v2: no thread record here, but the membership map still knows whether
    // this is a group thread — the group icon must survive the section move.
    const isGroup = isGroupParticipantCount(participantsByThread?.[row.threadId] ?? 1);
    const allPaused = group.rows.every((r) => r.paused);
    // Meta line: multi-routine groups lead with the count and fall back to
    // the primary's trigger summary when no clock exists; a SINGLE row's
    // summary already IS its cadence (trigger rows especially), so the
    // no-clock fallback must not repeat it ("X · X" — 2026-08-16 review).
    const clockLabel = allPaused ? 'Paused' : routineNextRunLabel(row.nextRunAt);
    const metaText = (group.rows.length > 1
      ? [`${group.rows.length} routines`, clockLabel || (row.kind === 'trigger' ? row.scheduleSummary : '')]
      : [row.scheduleSummary, clockLabel]
    ).filter(Boolean).join(' · ');
    return (
      <ThreadSidebarRow
        key={row.threadId}
        type="button"
        role="tab"
        aria-selected={isActive}
        aria-label={`routine ${isGroup ? 'group thread ' : ''}${row.name}`}
        data-sidebar-thread-id={row.threadId}
        data-routine-row-source="server"
        $active={isActive}
        title={row.name}
        onClick={() => {
          if (!isActive) onSwitch(row.threadId, { fallbackTitle: row.name });
        }}
      >
        <ThreadSidebarRowMain>
          <RoutineRowTitleLine>
            <RoutineGlyph active={!allPaused} size={12} />
            {isGroup && (
              <ThreadTypeGlyph kind="group" active={isActive} size={12} data-routine-group-glyph="" />
            )}
            <ThreadSidebarRowTitle $active={isActive}>{row.name}</ThreadSidebarRowTitle>
            {allPaused && (
              <RoutinePausedChip data-routine-paused-chip="">Paused</RoutinePausedChip>
            )}
          </RoutineRowTitleLine>
          {/* EMPTY-META SUPPRESSION (adversarial review finding 7,
              2026-08-03): a server row with no scheduleSummary AND a
              null/absent nextRunAt produced '' — an empty block occupying
              vertical space. No text → no element. */}
          {metaText && (
            <RoutineNextRunMeta data-routine-row-meta="">
              {metaText}
            </RoutineNextRunMeta>
          )}
        </ThreadSidebarRowMain>
      </ThreadSidebarRow>
    );
  };

  return (
    <ThreadSidebarContainer aria-label="threads" data-thread-sidebar="">
      <ThreadSidebarHeader>
        {/* Official brand treatment (round 3, 2026-08-01): the exact
            "prest[0]" wordmark the site Header renders — Space Grotesk 700 +
            the 0-logo image as the "0" — at sidebar scale. Replaces the plain
            "threads" label; the "+" keeps its top-right placement. */}
        <SidebarBrandWordmark data-sidebar-brand="" aria-label="prest0">
          prest
          <SidebarBrandLogoImage src={SIDEBAR_BRAND_LOGO_SRC} alt="0" />
        </SidebarBrandWordmark>
        <ThreadNewButton
          type="button"
          onClick={onNew}
          aria-label="start new thread"
          title="new thread"
        >
          +
        </ThreadNewButton>
      </ThreadSidebarHeader>
      {/* THREAD SEARCH (PR-5, 2026-09-09): one box over names (current and
          former) and messages; Enter searches everything on the server. */}
      {search && <ThreadSearch api={search} variant="rail" />}
      {/* Workspace header (owner directive 2026-08-19): the rail says WHOSE
          threads these are on every render — org dot + name (one shared
          label source with the header pill) — and, when threads from other
          organizations exist, the view toggle lives RIGHT HERE with the org
          name, as a high-contrast segmented control. Single-workspace users
          see only the identity line (zero extra chrome). */}
      {activeWorkspaceOption && (
        <WorkspaceHeaderBlock data-sidebar-workspace-header="">
          <WorkspaceIdentityLine data-sidebar-workspace-chip="" title={workspaceLabel(activeWorkspaceOption, accountUid)}>
            <WorkspaceContextDot
              $color={workspaceMarkColor(
                activeWorkspaceOption.accountId,
                (displayedWorkspaceOptions || []).map((w) => w.accountId),
              )}
              aria-hidden="true"
            />
            <WorkspaceIdentityName>{workspaceLabel(activeWorkspaceOption, accountUid)}</WorkspaceIdentityName>
          </WorkspaceIdentityLine>
          {(foreignThreadCount > 0 || combinedView) && onOpenForeignThread && (
            <WorkspaceViewToggle role="group" aria-label="thread view">
              <WorkspaceViewSegment
                type="button"
                $active={!combinedView}
                aria-pressed={!combinedView}
                data-workspace-view-this=""
                onClick={() => setCombinedView(false)}
              >
                this workspace
              </WorkspaceViewSegment>
              <WorkspaceViewSegment
                type="button"
                $active={combinedView}
                aria-pressed={combinedView}
                data-combined-view-toggle=""
                onClick={() => setCombinedView(true)}
              >
                all workspaces · {allThreads ? allThreads.length : 0}
              </WorkspaceViewSegment>
            </WorkspaceViewToggle>
          )}
        </WorkspaceHeaderBlock>
      )}
      <ThreadSidebarList
        ref={listRef}
        role="tablist"
        aria-orientation="vertical"
        onKeyDown={handleListKeyDown}
      >
        {/* EXTENDED ("all workspaces") VIEW — grouped BY ORGANIZATION (owner
            directive 2026-08-19; supersedes #909's strict-removal, whose
            containment invariant still holds: foreign rows run the SEALED
            organization switch, never an in-place render). One section per
            workspace (dot + name + count), the CURRENT workspace first, so
            every thread's org attachment is structural — no per-row badge
            noise. Workspace options appear ONLY here, in the extended view.
            Rows carry data-sidebar-thread-id so arrow-key navigation
            includes them. */}
        {combinedView && combinedRows && (() => {
          const rosterIds = (displayedWorkspaceOptions || []).map((w) => w.accountId);
          // ORG-NAME FALLBACK CHAIN (owner directive 2026-08-19: "the
          // organization name needs to be displayed, not just 'another
          // workspace'"): verified options → live workspace_index name →
          // neutral label. Never a raw account id.
          const labelForAccount = (accountId: string | null): string => {
            if (!accountId) {
              return activeWorkspaceOption
                ? workspaceLabel(activeWorkspaceOption, accountUid)
                : 'this workspace';
            }
            const opt = displayedWorkspaceOptions?.find((w) => w.accountId === accountId);
            if (opt) return workspaceLabel(opt, accountUid);
            const liveName = (liveWorkspaceNames[accountId] || '').trim();
            return liveName || 'another workspace';
          };
          // Active workspace's section also carries the unbound legacy
          // threads (they live in its default view); foreign sections sort
          // by display name for a stable scan order.
          const groups = new Map<string, ChatThread[]>();
          for (const t of combinedRows) {
            const key = t.accountId && activeWorkspaceId && t.accountId !== activeWorkspaceId
              ? t.accountId
              : '__active__';
            const bucket = groups.get(key);
            if (bucket) bucket.push(t); else groups.set(key, [t]);
          }
          // GHOST SECTIONS (task #33, owner report 2026-08-20): a section
          // whose accountId resolves to NO verified workspace row is an org
          // the user cannot actually open — its switch dead-ends, and its
          // name came from a stale label mirror at best. Hide those sections
          // once the verified roster has loaded (never on the empty roster
          // of a still-loading paint — hiding must not flicker). View-level
          // only: authorization is server-enforced and the threads stay in
          // their own workspace.
          const verifiedSectionIds = new Set(rosterIds);
          const rosterReady = verifiedSectionIds.size > 0;
          const foreignKeys = Array.from(groups.keys())
            .filter((k) => k !== '__active__')
            .filter((k) => !rosterReady || verifiedSectionIds.has(k))
            .sort((a, b) => labelForAccount(a).localeCompare(labelForAccount(b)));
          const sectionKeys = (['__active__', ...foreignKeys] as string[])
            .filter((k) => (groups.get(k) || []).length > 0);
          return sectionKeys.map((key) => {
            const isActiveSection = key === '__active__';
            const sectionAccountId = isActiveSection ? (activeWorkspaceId || null) : key;
            const rows = groups.get(key) || [];
            const sectionLabel = labelForAccount(isActiveSection ? null : key);
            return (
              <React.Fragment key={`combined_section_${key}`}>
                <SidebarSectionHeader data-sidebar-section={isActiveSection ? 'workspace-current' : `workspace-${key}`}>
                  {sectionAccountId && (
                    <WorkspaceContextDot
                      $color={workspaceMarkColor(sectionAccountId, rosterIds)}
                      aria-hidden="true"
                    />
                  )}
                  {sectionLabel}
                  <SidebarSectionCount>· {rows.length}{isActiveSection ? ' · current' : ''}</SidebarSectionCount>
                </SidebarSectionHeader>
                {rows.map((t) => {
                  const foreign = !isActiveSection;
                  return (
                    <ThreadSidebarRow
                      key={`combined_${t.id}`}
                      type="button"
                      role="tab"
                      aria-selected={t.id === activeThreadId}
                      data-combined-row-id={t.id}
                      data-sidebar-thread-id={t.id}
                      $active={t.id === activeThreadId}
                      title={foreign
                        ? `opens in ${sectionLabel} — switches organization`
                        : t.topicSummary || t.title}
                      onClick={() => {
                        if (foreign) {
                          onOpenForeignThread?.(t);
                        } else if (t.id !== activeThreadId) {
                          onSwitch(t.id);
                        }
                      }}
                    >
                      <ThreadSidebarRowMain>
                        <RoutineRowTitleLine>
                          <ThreadSidebarRowTitle $active={t.id === activeThreadId}>{t.title}</ThreadSidebarRowTitle>
                        </RoutineRowTitleLine>
                        <ThreadSidebarRowMeta>
                          {relativeActivityLabel(t.lastActiveAt)}
                          {foreign ? ' · switches organization' : ''}
                        </ThreadSidebarRowMeta>
                      </ThreadSidebarRowMain>
                      {foreign && (
                        <ForeignSwitchGlyph aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 12 12">
                            <path d="M4.5 2 L8.5 6 L4.5 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </ForeignSwitchGlyph>
                      )}
                    </ThreadSidebarRow>
                  );
                })}
              </React.Fragment>
            );
          });
        })()}
        {/* Sections (round 3, 2026-08-01): "routines" → "group threads" →
            "threads", each rendered ONLY when non-empty, every thread in
            exactly one (precedence routines > group threads > threads). All
            three headers share SidebarSectionHeader — icon + lowercase label
            + count (+ the routines section's manage action). The extended
            (all-workspaces) view REPLACES these sections while toggled. */}
        {!combinedView && routineUnionRows.length > 0 && (
          <>
            <SidebarSectionHeader data-sidebar-section="routines">
              <RoutineGlyph size={12} />
              routines
              {/* PARITY (2026-08-02): the count is the UNION length — by
                  construction exactly the number of rows the manager panel
                  shows for the same inputs (one shared builder, one shared
                  listRoutines snapshot). */}
              <SidebarSectionCount>· {routineUnionRows.length}</SidebarSectionCount>
              {/* lowercase label per the rail's platform style ("threads",
                  "exit full screen") — quiet ghost affordance, restyled
                  2026-08-01 (production: the pill read as a foreign button) */}
              <RoutineManageButton
                type="button"
                data-routine-manage-btn=""
                aria-label="manage routines"
                title="manage routines"
                onClick={() => setRoutineManagerOpen(true)}
              >
                manage
              </RoutineManageButton>
            </SidebarSectionHeader>
            {/* Per-THREAD groups of the per-routine union (v2): a group
                whose thread doc is loaded keeps the rich thread-row
                rendering (spinner, unread, pin, ×); a live routine thread
                outside the listen window renders the server-row skin. */}
            {routineThreadGroups.map((group) => {
              const record = threadsById.get(group.threadId);
              return record ? renderThreadRow(record, group) : renderServerRoutineRow(group);
            })}
          </>
        )}
        {/* PR-5 (2026-09-09): categories → "direct messages" → "group
            threads" → "threads", from the shared builder; the two legacy
            section hooks keep their values. */}
        {!combinedView && threadSections.map((section) => (
          <React.Fragment key={section.key}>
            <ThreadSectionHeader
              section={section}
              hook={sidebarSectionHook(section)}
              hookAttr="data-sidebar-section"
              organize={organize}
              index={visibleSectionIds.indexOf(section.categoryId)}
              total={visibleSectionIds.length}
              visibleSectionIds={visibleSectionIds}
              manageSections={allSections}
              dropActive={drag.over === section.categoryId}
              dropHandlers={drag.sectionProps(section.categoryId)}
            />
            {!organize?.collapsed.has(section.categoryId) && section.rows.map((t) => renderThreadRow(t))}
          </React.Fragment>
        ))}
        {organize && (
          <ThreadListNotice notice={organize.notice} lang={organize.lang} onDismiss={organize.dismissNotice} />
        )}
      </ThreadSidebarList>
      {/* Bottom-pinned footer (fullscreen exit + account menu, Miles
          2026-07-13): a labeled way OUT of fullscreen plus the account menu,
          always in the same bottom-left spot. <=820px the whole sidebar is
          hidden and the header buttons remain the exit path. */}
      {(account || onExitFullScreen) && (
        <ThreadSidebarFooter data-sidebar-footer="">
          {account && (
            <SidebarAccountMenuSection
              account={account}
              onNavigateWallet={onNavigateWallet}
              onNavigateSettings={onNavigateSettings}
              onSignOut={onSignOut}
            />
          )}
          {onExitFullScreen && (
            <SidebarExitFullScreenButton
              type="button"
              data-sidebar-exit-fullscreen=""
              aria-label="exit full screen"
              title="exit full screen"
              onClick={onExitFullScreen}
            >
              <CollapseSvgIcon />
              exit full screen
            </SidebarExitFullScreenButton>
          )}
        </ThreadSidebarFooter>
      )}
      {/* Routine manager (body portal) — opened by the section's Manage
          button; rows stream live from the SAME `threads` prop AND the SAME
          accountRoutines snapshot the rail section renders (parity by
          construction, 2026-08-02). Open rows switch via the sidebar's own
          onSwitch (one navigation source). */}
      {routineManagerOpen && (
        <RoutineManagerPanel
          /* The SAME tombstone-filtered inputs the rail section built its
             union from (finding 4) — parity is structural, so an archived
             routine can never linger in one surface but not the other. */
          threads={routineSourceThreads}
          accountRoutines={visibleAccountRoutines}
          onOpenThread={onSwitch}
          onClose={() => setRoutineManagerOpen(false)}
          onRoutineMirrorPatch={onRoutineMirrorPatch}
        />
      )}
    </ThreadSidebarContainer>
  );
};

export default ThreadSidebar;
